import taskApi from '@/api/task';
import { delayTimeOptions } from '@/constants/selectOptions';
import { searchIcon, selectIcon } from '@/constants/static';
import { Modal, Button, Input, Select, Row, Col, Dropdown, Space, message, Spin } from 'antd';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useState } from 'react';
interface taskerItem {
    taskerId: string;
    fullName: string;
    online: boolean;
    onlineTime: string;
    state: string;
    avatarUrl: string;
    ratingInfo: {
        totalRatingCount: string;
        ratingScore: string;
    };
}
const AssignTaskerModal = ({
    taskId,
    workEndTime,
    onCancel,
}: {
    taskId: string;
    workEndTime: string | null;
    onCancel: () => void;
}) => {
    const [searchParams, setParams] = useState({ taskerName: '', type: '' });
    const debouncedSave = useCallback(
        debounce(nextValue => setParams(nextValue), 1500),
        []
    );
    const [taskerList, setTaskerList] = useState<taskerItem[]>([]);
    const [sortKey, setSort] = useState(0);
    const sortItems = [
        { label: 'By last online', key: 0 },
        { label: 'By tasker name a～z', key: 1 },
        { label: 'By tasker name z～a', key: 2 },
    ];
    const [loading, setLoading] = useState('');
    const getTaskers = async () => {
        setLoading('list');
        try {
            const res = await taskApi.getAssignTaskList({ taskerName: searchParams.taskerName });
            sortData(sortKey, res.data);
        } catch (error: any) {
            message.error(error);
            setLoading('');
        } finally {
            setLoading('');
        }
    };
    const [timeLeft, setTimeLeft] = useState(0);
    useEffect(() => {
        if (taskId) {
            getTaskers();
            if (workEndTime) {
                const nowUnix = dayjs().unix();
                const endUnix = workEndTime ? dayjs(workEndTime).unix() : 0;
                console.log(endUnix - nowUnix);
                setTimeLeft(endUnix - nowUnix);
            } else {
                setTimeLeft(0);
            }
        }
    }, [taskId, searchParams]);
    const [delayTime, setDelayTime] = useState(null);
    const [taskerId, setTaskerId] = useState('');
    const onAssign = async () => {
        setLoading('assign');
        try {
            const res = await taskApi.assignTasker(taskerId, { taskId, delayTime });
            message.success('The task has been sent to this tasker');
            closeModal();
        } catch (error: any) {
            message.error(error);
            setLoading('');
        } finally {
            setLoading('');
        }
    };
    const closeModal = () => {
        setTaskerList([]);
        setDelayTime(null);
        setTaskerId('');
        onCancel();
        setParams({ taskerName: '', type: '' });
        setSort(0);
    };
    const handleMenuClick = e => {
        if (Number(e.key) !== sortKey) {
            setSort(Number(e.key));
            sortData(Number(e.key), taskerList);
        }
    };
    const sortData = (key: number, list: taskerItem[]) => {
        setTaskerList(() => {
            const newItems = [...list];
            newItems.sort((a, b) => {
                switch (key) {
                    case 1:
                        return sortByNameEnhanced(a.fullName, b.fullName, 'asc');
                    case 2:
                        return sortByNameEnhanced(a.fullName, b.fullName, 'desc');
                    default:
                        return (
                            (b.onlineTime ? dayjs(b.onlineTime).unix() : 0) -
                            (a.onlineTime ? dayjs(a.onlineTime).unix() : 0)
                        );
                }
            });
            return newItems;
        });
    };
    const sortByNameEnhanced = (NAMEA: string, NAMEB: string, direction: 'asc' | 'desc') => {
        // 处理空值情况（空值排最后）
        if (!NAMEA && !NAMEB) return 0;
        if (!NAMEA) return direction === 'asc' ? 1 : -1; // a是空值，排后面
        if (!NAMEB) return direction === 'desc' ? 1 : -1; // b是空值，排前面
        const nameA = (NAMEA || '').toString().toLowerCase();
        const nameB = (NAMEB || '').toString().toLowerCase();

        const isLetterA = /^[a-z]/.test(nameA);
        const isLetterB = /^[a-z]/.test(nameB);

        // 比较结果
        let comparison = 0;

        if (isLetterA && isLetterB) {
            comparison = nameA.localeCompare(nameB);
        } else if (isLetterA) {
            comparison = -1;
        } else if (isLetterB) {
            comparison = 1;
        } else {
            comparison = nameA.localeCompare(nameB);
        }

        return direction === 'asc' ? comparison : -comparison;
    };
    return (
        <Modal width={440} open={taskId ? true : false} closeIcon={<></>} destroyOnClose centered maskClosable={false}>
            <div className="modal-title">Assign to a Tasker for Review</div>
            <Select
                className="assign-selece"
                style={{ height: 46, width: '100%', marginTop: 32 }}
                placeholder="Set wait time for tasker acceptance"
                suffixIcon={selectIcon}
                options={delayTimeOptions.filter(i => (workEndTime ? Number(i.value) < timeLeft : true))}
                onChange={v => setDelayTime(v)}
            />
            <div className="assigned flex-between" style={{ display: taskerId ? 'flex' : 'none' }}>
                <div>
                    <img
                        width={32}
                        height={32}
                        style={{ borderRadius: '50%', marginRight: 16 }}
                        src={
                            taskerList.find(i => i.taskerId === taskerId)?.avatarUrl ||
                            require('@/assets/images/default-avatar.png')
                        }
                        alt="tasker-avatar"
                    />
                    <span>{taskerList.find(i => i.taskerId === taskerId)?.fullName || '-'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <div className="tasker-score flex-center">
                        <img src={require('@/assets/images/star.png')} alt="" />
                        {taskerList.find(i => i.taskerId === taskerId)?.ratingInfo.ratingScore}
                    </div>
                    <img
                        width={24}
                        style={{ opacity: '0.5', marginLeft: 16, cursor: 'pointer' }}
                        src={require('@/assets/images/Icons_Cross.png')}
                        alt=""
                        onClick={() => setTaskerId('')}
                    />
                </div>
            </div>
            <Input
                className="assign-input"
                style={{ display: taskerId ? 'none' : 'inline-flex' }}
                placeholder="Search by tasker"
                onChange={e => debouncedSave({ ...searchParams, taskerName: e.target.value })}
                prefix={searchIcon}
            />
            <div
                className="log-table"
                style={{ padding: '13px 18px', marginTop: 0, display: taskerId ? 'none' : 'block' }}
            >
                <div className="tasker-sort">
                    <Dropdown menu={{ items: sortItems, onClick: handleMenuClick }}>
                        <Space size={8}>
                            {sortItems[sortKey].label}
                            {selectIcon}
                        </Space>
                    </Dropdown>
                </div>

                {loading === 'list' ? (
                    <div style={{ height: 360 }} className="flex-center">
                        <Spin />
                    </div>
                ) : (
                    <div className="assign-taskers">
                        {taskerList.map(i => {
                            return (
                                <Row
                                    className="table-tr"
                                    key={i.taskerId}
                                    onClick={() => setTaskerId(i.taskerId)}
                                    style={{
                                        cursor: 'pointer',
                                        border: i.taskerId === taskerId ? '1px solid #E4E7EC' : 'none',
                                        borderRadius: 8,
                                        paddingLeft: 10,
                                    }}
                                >
                                    <Col span={14} className="flex-center">
                                        <span
                                            className="oline"
                                            style={{ backgroundColor: i.online ? '#7DB8A4' : '#F94D85' }}
                                        ></span>
                                        <img
                                            src={i.avatarUrl || require('@/assets/images/default-avatar.png')}
                                            alt="tasker-avatar"
                                        />
                                        <span style={{ flex: 1 }}>{i.fullName || '-'}</span>
                                    </Col>
                                    <Col span={4} style={{ textAlign: 'center' }}>
                                        {i.state || '-'}
                                    </Col>
                                    <Col span={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <div className="tasker-score flex-center">
                                            <img src={require('@/assets/images/star.png')} alt="" />
                                            {i.ratingInfo.ratingScore}
                                        </div>
                                    </Col>
                                </Row>
                            );
                        })}
                    </div>
                )}
            </div>
            <div className="flex-center" style={{ marginTop: 36, columnGap: 20 }}>
                <Button
                    className="submit-btn"
                    onClick={closeModal}
                    style={!delayTime || !taskerId ? {} : { color: '#F94D85', borderColor: '#F94D85' }}
                >
                    Cancel
                </Button>
                <Button
                    className="submit-btn"
                    loading={loading === 'assign'}
                    type="primary"
                    disabled={!delayTime || !taskerId}
                    onClick={onAssign}
                >
                    Assign task
                </Button>
            </div>
        </Modal>
    );
};
export default AssignTaskerModal;
